import api, { adminApiPrefix } from './Api';
import {
  GetProductsRequest,
  getProductsRequestToParams,
} from '../interfaces/Product/GetProductsRequest';
import {
  ProductSummary,
  productSummaryFromResponse,
} from '../interfaces/Product/ProductSummary';
import { PaginatedResponse } from '../interfaces/PaginatedResponse';
import {
  Product,
  productFromResponse,
  productToUpsertRequest,
} from '../interfaces/Product/Product';
import {
  ProductIdentifiers,
  productIdentifiersToUpdateRequest,
} from '../interfaces/ProductIdentifiers/ProductIdentifiers';

export const productsAdminApiPrefix = `${adminApiPrefix}/products`;

export const createProduct = async (product: Product): Promise<Product> => {
  const response = await api.post(
    productsAdminApiPrefix,
    productToUpsertRequest(product),
  );

  return productFromResponse(response.data?.data);
};

export const getProducts = async (
  getProductsRequest: GetProductsRequest,
): Promise<PaginatedResponse<ProductSummary>> => {
  const response = await api.get(productsAdminApiPrefix, {
    params: getProductsRequestToParams(getProductsRequest),
  });

  return {
    data: (response.data?.data ?? []).map((p: any) =>
      productSummaryFromResponse(p),
    ),
    totalCount: response.data?.total_count ?? 0,
  };
};

export const getProduct = async (id: number): Promise<Product> => {
  const response = await api.get(`${productsAdminApiPrefix}/${id}`);

  return productFromResponse(response.data?.data);
};

export const updateProduct = async (product: Product): Promise<Product> => {
  const response = await api.put(
    `${productsAdminApiPrefix}/${product.id}`,
    productToUpsertRequest(product),
  );

  return productFromResponse(response.data?.data);
};

export const updateProductSlug = async (
  id: number,
  slug: string,
): Promise<void> => api.put(`${productsAdminApiPrefix}/slug/${id}`, { slug });

export const updateProductStock = async (
  id: number,
  stock: number,
): Promise<Product> => {
  const response = await api.put(`${productsAdminApiPrefix}/stock/${id}`, {
    new_quantity: stock,
  });

  return productFromResponse(response.data?.data);
};

export const updateProductIdentifiers = async (
  updates: ProductIdentifiers[],
): Promise<void> =>
  api.put(`${productsAdminApiPrefix}/identifiers/batch`, {
    updates: updates.map(productIdentifiersToUpdateRequest),
  });

export const deleteProduct = async (id: number): Promise<void> =>
  api.delete(`${productsAdminApiPrefix}/${id}`);

export const downloadProductsExcel = async (): Promise<void> => {
  const response = await api.get(`${productsAdminApiPrefix}/download`, {
    responseType: 'blob', // Important for binary files
  });

  // Create a Blob from the response data
  const fileURL = window.URL.createObjectURL(new Blob([response.data]));
  const fileLink = document.createElement('a');
  fileLink.href = fileURL;

  // Default filename
  let filename = 'urunler.xlsx';

  // Extract filename from Content-Disposition header (if present)
  const contentDisposition = response.headers['content-disposition'];
  if (contentDisposition) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '');
    }
  }

  fileLink.setAttribute('download', filename);

  // Trigger the download
  document.body.appendChild(fileLink);
  fileLink.click();
  document.body.removeChild(fileLink);
  window.URL.revokeObjectURL(fileURL); // Clean up
};

export const uploadProductsExcel = async (file: File): Promise<void> => {
  const bodyFormData = new FormData();
  bodyFormData.append('file', file);

  return api.post(`${productsAdminApiPrefix}/upload`, bodyFormData);
};
